import { Link } from "react-router-dom";
import {
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  FileDown,
  FolderKanban,
  Loader2,
  Mail,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Estimate, Invoice } from "../../../lib/sales";
import { Btn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import type { AnyDoc, DocConfig, DocKind } from "./config";

/**
 * The document's actions, in the page head.
 *
 * Only the four that move the document forward stay visible - download, edit,
 * send and the kind's own next step (convert a proposal, settle an invoice).
 * Everything else lives behind the three-dot menu, which is where the status
 * changer and the delete belong: both are corrections rather than steps, and a
 * head of seven equal-weight buttons says nothing about which one you came for.
 *
 * The estimate is the one kind with TWO next steps, so it gets a fifth button:
 * a won quote can be billed and it can be started, and those are not
 * alternatives - most work is started first and billed later. Neither belongs
 * in the menu, which is for corrections.
 */
export function DocDetailActions({
  doc,
  cfg,
  kind,
  canManage,
  canProjects,
  canMail,
  busy,
  pdfBusy,
  onDownloadPdf,
  onSend,
  onConvert,
  onCreateProject,
  onMarkPaid,
  onStatus,
  onDelete,
}: {
  doc: AnyDoc;
  cfg: DocConfig;
  kind: DocKind;
  canManage: boolean;
  /**
   * `manage:crm.projects`, which is NOT the right that opened this page.
   *
   * The whole sales section answers to `manage:crm.sales` (`crm/nav.ts`), and
   * the create-project route answers to the projects one instead. A sales-only
   * operator gated on `canManage` would get a button that renders and then 403s,
   * and the link to an existing project would take them to a section their nav
   * does not even list.
   */
  canProjects: boolean;
  /** `manage:crm.email`: the cover-letter send opens the mailbox, which needs it. */
  canMail: boolean;
  busy: boolean;
  pdfBusy: boolean;
  onDownloadPdf: () => void;
  onSend: () => void;
  onConvert: () => void;
  onCreateProject: () => void;
  onMarkPaid: () => void;
  onStatus: (status: string) => void;
  onDelete: () => void;
}) {
  const t = useT();
  const close = useClosePops();

  const inv = doc as Invoice;
  const settled = inv.status === "paid" || inv.status === "cancelled";
  /**
   * The project this estimate already started, if it did.
   *
   * Read from the document rather than discovered by pressing the button and
   * being refused: the server's 409 is the last line of defence against two
   * projects for one quote, not the way the operator is supposed to find out.
   * A button that cannot succeed is worse than no button, and the link that
   * replaces it is what they wanted anyway.
   */
  const startedProjectId = cfg.canStartProject ? (doc as Estimate).projectId : null;
  // The status is derived from the payments on an invoice, so setting it by
  // hand there would just be overwritten by the next receipt. Every other kind
  // has no such source and needs the menu.
  const canSetStatus = canManage && !cfg.hasPayments;

  return (
    <>
      {/* Available to everybody who can open the document: a viewer with no
          manage right still has to be able to hand the client a copy. */}
      <Btn onClick={onDownloadPdf} disabled={pdfBusy}>
        {pdfBusy ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
        {t("sales.downloadPdf")}
      </Btn>

      {canManage ? (
        <>
          <Link to={`/sales/${kind}/${doc.id}/edit`} className="crm-btn">
            <Pencil className="size-4" />
            {t("sales.edit")}
          </Link>

          {cfg.canSend && cfg.mailCompose && canMail ? (
            // Two ways to send, so one button with a choice rather than a sixth
            // button in the head. The cover letter is first: it is the one a
            // person writing to a client means, and the fixed notice stays for
            // a quick resend.
            <Pop
              trigger={(open) => (
                <Btn aria-haspopup="menu" aria-expanded={open} disabled={busy}>
                  <Send className="size-4" />
                  {t("sales.sendMenu")}
                  <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
                </Btn>
              )}
            >
              <Link to={`/email?estimate=${doc.id}`} className="crm-mi" onClick={() => close()}>
                <Mail className="size-4" />
                {t("sales.sendWithLetter")}
              </Link>
              <button
                type="button"
                className="crm-mi"
                onClick={() => {
                  close();
                  onSend();
                }}
              >
                <Send className="size-4" />
                {t("sales.sendStandard")}
              </button>
            </Pop>
          ) : cfg.canSend ? (
            <Btn onClick={onSend} disabled={busy}>
              <Send className="size-4" />
              {t("sales.send")}
            </Btn>
          ) : null}

          {cfg.convert ? (
            <Btn tone="dark" onClick={onConvert} disabled={busy}>
              <ArrowRightLeft className="size-4" />
              {t(cfg.convert.labelKey)}
            </Btn>
          ) : null}

          {cfg.canStartProject && canProjects ? (
            startedProjectId ? (
              <Link to={`/projects/${startedProjectId}`} className="crm-btn">
                <FolderKanban className="size-4" />
                {t("projects.open")}
              </Link>
            ) : (
              <Btn onClick={onCreateProject} disabled={busy}>
                <FolderKanban className="size-4" />
                {t("projects.createFromEstimate")}
              </Btn>
            )
          ) : null}

          {cfg.hasPayments && !settled ? (
            <Btn tone="brand" onClick={onMarkPaid} disabled={busy}>
              <CheckCircle2 className="size-4" />
              {t("sales.markPaid")}
            </Btn>
          ) : null}

          <Pop
            trigger={(open) => (
              <button
                type="button"
                className={`crm-cb${open ? " is-on" : ""}`}
                title={t("common.more")}
                aria-label={t("common.more")}
                aria-haspopup="menu"
                aria-expanded={open}
              >
                <MoreHorizontal className="size-4" />
              </button>
            )}
          >
            {canSetStatus ? (
              <>
                <div className="crm-menu-head">
                  <div className="crm-menu-t">{t("sales.changeStatus")}</div>
                </div>
                {cfg.statuses.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`crm-mi${s === doc.status ? " is-on" : ""}`}
                    onClick={() => {
                      close();
                      if (s !== doc.status) onStatus(s);
                    }}
                  >
                    {t(`sales.status.${s}`)}
                  </button>
                ))}
                <div className="crm-menu-sep" />
              </>
            ) : null}

            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onDelete();
              }}
            >
              <Trash2 className="size-4" />
              {t("sales.delete")}
            </button>
          </Pop>
        </>
      ) : null}
    </>
  );
}
